import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, Form, Input, InputNumber, Modal, Segmented, Select, Space, Switch, Tag, Typography } from 'antd';

import ScheduleFields, { browserTimezone } from './ScheduleFields';
import ExportOptionsFields, {
	STORED_LIST_OPTIONS,
	buildStoredOptions,
	controlsFromStoredOptions,
} from '../common/ExportOptionsFields';
import FilterChips from '../common/FilterChips';
import ToggleRow from '../common/ToggleRow';
import UserRef from '../users/UserRef';
import { UserOptionRow } from '../users/UserSearchSelect';
import { createExportDefinition, patchExportDefinition } from '../../services/exportDefinitions';
import {
	CHANNEL_REASON_WORDS,
	DISABLED_REASON_WORDS,
	EMAIL_RE,
	SUBJECT_KIND_OPTIONS,
	draftFromDefinition,
	formatsFor,
	limitsOf,
	payloadFromDraft,
	placeExportError,
	subjectFromDraft,
	validateDraft,
} from '../../utils/exportDefinitions';
import { buildFilterFields } from '../../utils/kqlBuilder';
import { normalizeId } from '../../utils/ids';
import { cardsHoldRepeatingGroup } from '../../utils/groupColumns';
import { getUserId } from '../../utils';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import { CLOSE, PLUS } from '../icons';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';
import useElementChoices from '../../hooks/useElementChoices';
import QueryRefusalPick from '../items/QueryRefusalPick';

const { Text } = Typography;

/**
 * Authoring a STORED export (keepr-api docs/SCHEMA.md § Stored exports):
 * what it is of, in what format, when it runs, who receives it, and how long
 * its file is kept.
 *
 * A modal, like the automation rule editors beside it on the settings page —
 * it shares their cadence controls (`ScheduleFields`) verbatim — and its
 * format questions are the export dialog's own (`ExportOptionsFields`), so a
 * saved export and a one-off download can never drift into two vocabularies.
 *
 * ⚠ OWNERSHIP. A run renders through its definition's OWNER, and any save
 * makes the person saving the owner. When that is not already the reader,
 * the editor says so before they press Save — never after.
 *
 * ⚠ RCS is drawn UNAVAILABLE from the listing's `channels.rcs` (never a
 * switch that does nothing), and always sent as off.
 */

const ITEMS_EVERYTHING = '__params__';

const BUCKET_WORDS = { day: 'day', week: 'week', month: 'month' };

/** A stored pivot in words: "One row per Driver, a column per day of Created." */
export function describePivot( matrix, labelFor = name => name ) {
	if ( ! matrix?.rows ) return '';
	const field = matrix.field && matrix.field !== 'createdAt' ? labelFor( matrix.field ) : 'Created';
	const parts = [
		`One row per ${ labelFor( matrix.rows ) }`,
		`a column per ${ BUCKET_WORDS[ matrix.bucket ] || 'day' } of ${ field }`,
	];
	if ( matrix.group ) parts.push( `grouped by ${ labelFor( matrix.group ) }` );
	parts.push( matrix.cell ? `each cell showing ${ labelFor( matrix.cell ) }` : 'each cell a tick' );
	return `${ parts.join( ', ' ) }.`;
}

const defaultSchedule = () => ( {
	every: 1,
	unit: 'week',
	daysOfWeek: [ 1 ],
	time: '07:00',
	timezone: browserTimezone(),
} );

const currentUserId = () => {
	const id = getUserId();
	return id && id !== 'undefined' ? id : null;
};

export default function ExportDefinitionEditor( {
	open,
	collectionId,
	definition = null,
	// A new definition's starting point: a prefill from the export dialog
	// ({ name, subject, format, options }) or a duplicate.
	prefill = null,
	savedFilters = [],
	cards = [],
	getElementsForCard,
	recipientOptions = [],
	recipientsLoading = false,
	limits,
	channels,
	onClose,
	onSaved,
} ) {
	const { message } = App.useApp();
	const bounds = limitsOf( limits );
	const me = currentUserId();
	const isEdit = Boolean( definition?._id );
	const source = isEdit ? definition : prefill;

	const seed = useCallback( () => draftFromDefinition( source, {
		limits,
		defaultSchedule: defaultSchedule(),
		defaultRecipients: me ? [ { type: 'user', userId: me } ] : [],
	} ), [ source, limits, me ] );

	const [ draft, setDraft ] = useState( seed );
	const [ controls, setControls ] = useState( () => controlsFromStoredOptions( source?.options ) );
	const [ errors, setErrors ] = useState( {} );
	const [ formError, setFormError ] = useState( null );
	const [ saving, setSaving ] = useState( false );
	const [ newAddress, setNewAddress ] = useState( '' );
	// Bumped with every re-seed: the seeded draft is what typing is measured
	// against by the unsaved-changes guard.
	const [ seedVersion, setSeedVersion ] = useState( 0 );

	// Re-seed on every opening: reopened for another definition, the dialog
	// must not inherit the last one's draft.
	useEffect( () => {
		if ( ! open ) return;
		setDraft( seed() );
		setControls( controlsFromStoredOptions( source?.options ) );
		setErrors( {} );
		setFormError( null );
		setNewAddress( '' );
		setSeedVersion( version => version + 1 );
	}, [ open ] ); // eslint-disable-line react-hooks/exhaustive-deps

	const dirtySnapshot = useMemo( () => ( { draft, controls, newAddress } ), [ draft, controls, newAddress ] );
	const { resetDirty, guardDiscard } = useDirtyForm( dirtySnapshot, { enabled: open, baselineKey: seedVersion } );
	// Cancel, the ✕, the mask and Esc all ask first when something was typed.
	const handleCancel = useCallback( () => guardDiscard( () => onClose?.() ), [ guardDiscard, onClose ] );

	const patch = useCallback( next => setDraft( prev => ( { ...prev, ...next } ) ), [] );
	const patchControls = useCallback( next => setControls( prev => ( { ...prev, ...next } ) ), [] );

	// The owner question, asked BEFORE Save. A new definition is the saver's
	// by construction; a stored one moves to the saver if it is not theirs.
	const ownerId = isEdit ? normalizeId( definition?.ownerId ) : null;
	const takesOwnership = isEdit && ( ! ownerId || ownerId !== me );

	const kind = draft.subjectKind;
	const kindOptions = useMemo( () => SUBJECT_KIND_OPTIONS.filter(
		// One item is authored where the item is; the editor keeps an item
		// subject it was given but does not offer to pick one.
		option => option.value !== 'item' || source?.subject?.kind === 'item'
	), [ source ] );

	const selectedFilter = useMemo(
		() => ( draft.itemsSource === 'filter'
			? savedFilters.find( row => normalizeId( row?._id ) === draft.savedFilterId ) || null
			: null ),
		[ savedFilters, draft.itemsSource, draft.savedFilterId ]
	);
	const filterReport = kind === 'items' && selectedFilter?.report?.rows ? selectedFilter.report : null;
	// A definition can carry its OWN pivot in `options.matrix` (valid on any
	// items subject; authored through the API or an assistant). This editor
	// cannot author one, but it must not lose one: it is kept exactly as
	// stored and shown read-only below the format. A saved filter's report,
	// when the subject carries one, is the live shape and wins.
	const storedPivot = useMemo( () => {
		const matrix = source?.options?.matrix;
		return matrix && typeof matrix === 'object' && matrix.rows ? matrix : null;
	}, [ source ] );
	const report = filterReport || ( kind === 'items' ? storedPivot : null );
	const pivotIsStored = Boolean( report ) && ! filterReport;

	// A list's query, carried over from the list it was saved from, reads as
	// the same chips the list shows it with.
	const filterFields = useMemo( () => ( draft.params?.q
		? buildFilterFields( { cards, getElementsForCard } )
		: [] ), [ draft.params?.q, cards, getElementsForCard ] );

	// An element's label wherever a card of this collection names it.
	const labelFor = useCallback( ( name ) => {
		for ( const card of cards ) {
			const elements = typeof getElementsForCard === 'function'
				? getElementsForCard( normalizeId( card?._id ) ) || []
				: card?.elements || [];
			const found = elements.find( el => el?.name === name );
			if ( found ) return found.label?.singular || name;
		}
		return name;
	}, [ cards, getElementsForCard ] );

	// Excel's "one line per entry" (KPR-685), offered when a card of this
	// collection holds a repeating group — or a stored answer is to be kept.
	const offersGroupShape = useMemo( () => controls.groupShape === 'entries'
		|| cardsHoldRepeatingGroup( cards, getElementsForCard ), [ controls.groupShape, cards, getElementsForCard ] );

	const itemsValue = draft.itemsSource === 'filter' ? draft.savedFilterId : ITEMS_EVERYTHING;
	const itemsOptions = useMemo( () => ( [
		{ value: ITEMS_EVERYTHING, label: 'Items of this collection' },
		...savedFilters.map( row => ( {
			value: normalizeId( row?._id ),
			label: row?.scope === 'user' ? `Saved filter: ${ row?.name } (yours)` : `Saved filter: ${ row?.name }`,
		} ) ),
	] ), [ savedFilters ] );

	const cardOptions = useMemo( () => cards.map( card => ( {
		value: normalizeId( card?._id ),
		label: card?.name || 'Untitled card',
	} ) ), [ cards ] );

	const handleKindChange = value => {
		const formats = formatsFor( value );
		patch( {
			subjectKind: value,
			format: formats.includes( draft.format ) ? draft.format : 'pdf',
		} );
		setErrors( prev => ( { ...prev, subject: undefined, format: undefined } ) );
	};

	const handleItemsChange = value => {
		if ( value === ITEMS_EVERYTHING ) patch( { itemsSource: 'params', savedFilterId: '' } );
		else patch( { itemsSource: 'filter', savedFilterId: value } );
		// A report grid belongs to the filter that carries the preset — or to
		// the definition's own stored pivot, which any subject keeps.
		if ( controls.contents === 'matrix' && ! storedPivot ) patchControls( { contents: 'table' } );
		setErrors( prev => ( { ...prev, subject: undefined } ) );
	};

	const recipientCount = draft.accountIds.length + draft.addresses.length;

	const addAddress = () => {
		const address = newAddress.trim().toLowerCase();
		if ( ! address ) return;
		if ( ! EMAIL_RE.test( address ) ) {
			setErrors( prev => ( { ...prev, recipients: 'That is not an email address.' } ) );
			return;
		}
		if ( draft.addresses.some( entry => entry.address === address ) ) {
			setErrors( prev => ( { ...prev, recipients: 'That address is already on the list.' } ) );
			return;
		}
		if ( draft.addresses.length >= bounds.maxExternalRecipients ) {
			setErrors( prev => ( { ...prev, recipients: `At most ${ bounds.maxExternalRecipients } addresses without a keepr account.` } ) );
			return;
		}
		if ( recipientCount >= bounds.maxRecipients ) {
			setErrors( prev => ( { ...prev, recipients: `An export reaches at most ${ bounds.maxRecipients } recipients.` } ) );
			return;
		}
		setErrors( prev => ( { ...prev, recipients: undefined } ) );
		patch( { addresses: [ ...draft.addresses, { address } ] } );
		setNewAddress( '' );
	};

	// The account picker offers the collection's collection-wide
	// collaborators only (the API refuses anyone else, 400
	// recipient_not_collaborator). A stored recipient who has since lost that
	// access still shows, by name, so it can be seen and removed.
	const accountOptions = useMemo( () => {
		const known = new Map( recipientOptions.map( user => [ normalizeId( user?._id ), user ] ) );
		const options = recipientOptions.map( user => ( {
			value: normalizeId( user._id ),
			label: <UserOptionRow user={user} />,
			searchText: `${ user?.fullName || '' } ${ user?.firstName || '' } ${ user?.lastName || '' } ${ user?.email || '' }`.toLowerCase(),
		} ) );
		draft.accountIds.filter( id => ! known.has( id ) ).forEach( id => options.push( {
			value: id,
			label: <UserRef userId={id} size={20} />,
			searchText: '',
		} ) );
		return options;
	}, [ recipientOptions, draft.accountIds ] );

	const rcs = channels?.rcs || { available: false, reason: 'channel_not_built' };
	const email = channels?.email || { available: true };

	// An element the export's filter could mean several of (400
	// `ambiguous_element`, element ids step 3): the person picks one, or any
	// of these cards, and the export is saved again with the pick.
	// …answering exactly the subject it was made for: any change forgets it.
	const elementPicks = useElementChoices( JSON.stringify( subjectFromDraft( draft ) ) );

	const handleSave = async () => {
		setFormError( null );
		const found = validateDraft( draft, { limits } );
		if ( kind === 'items' && controls.contents === 'matrix' && ! report && draft.format === 'pdf' ) {
			found.format = 'A report grid needs a saved filter that carries a report.';
		}
		setErrors( found );
		if ( Object.values( found ).some( Boolean ) ) return;

		const options = buildStoredOptions( {
			format: draft.format,
			contents: controls.contents,
			attachmentList: controls.attachmentList,
			embedAttachments: controls.embedAttachments,
			blankForm: controls.blankForm,
			isItemsTarget: kind === 'items',
			supportsAttachmentOptions: kind === 'item',
			report,
			reportFromFilter: Boolean( filterReport ),
			groupShape: controls.groupShape,
		} );
		const payload = payloadFromDraft( draft, options );

		setSaving( true );
		try {
			const saved = isEdit
				? await patchExportDefinition( collectionId, normalizeId( definition._id ), elementPicks.withChoices( payload ) )
				: await createExportDefinition( collectionId, elementPicks.withChoices( payload ) );
			elementPicks.reset();
			message.success( isEdit ? 'Export saved.' : 'Export created.' );
			resetDirty();
			onSaved?.( saved );
		}
		catch ( err ) {
			if ( elementPicks.take( err ) ) return;
			const placed = placeExportError( err );
			if ( placed.field ) setErrors( prev => ( { ...prev, [ placed.field ]: placed.message } ) );
			else setFormError( placed.message );
		}
		finally {
			setSaving( false );
		}
	};

	const fieldStatus = key => ( errors[ key ] ? 'error' : undefined );

	return (
		<Modal
			open={open}
			onCancel={handleCancel}
			width={720}
			centered
			destroyOnClose
			className='export-editor'
			title={isEdit ? 'Edit export' : 'New export'}
			footer={[
				<Button key='cancel' onClick={handleCancel}>Cancel</Button>,
				<Button key='save' type='primary' loading={saving} onClick={handleSave}>
					{isEdit ? 'Save export' : 'Create export'}
				</Button>,
			]}
		>
			<Form layout='vertical' className='export-editor__form' component='div'>

				{takesOwnership ? (
					<Alert
						type='warning'
						showIcon
						className='export-editor__alert'
						message='Saving makes you the owner; this export will run with your access.'
						description={ownerId ? (
							<span>
								It runs with its owner&apos;s access today — <UserRef userId={ownerId} size={20} />. After you save, what it can see and send is what you can see.
							</span>
						) : null}
					/>
				) : null}

				{isEdit && definition?.disabledReason ? (
					<Alert
						type='info'
						showIcon
						className='export-editor__alert'
						message={DISABLED_REASON_WORDS[ definition.disabledReason ] || 'Switched off by keepr.'}
						description='Saving does not switch it back on. Use the switch in the list when it is ready.'
					/>
				) : null}

				<Form.Item
					label='Name'
					required
					validateStatus={fieldStatus( 'name' )}
					help={errors.name || 'Names the export in this list and in the email subject.'}
				>
					<Input
						value={draft.name}
						onChange={event => patch( { name: event.target.value } )}
						placeholder='Month-end mileage'
						maxLength={100}
						aria-label='Name'
					/>
				</Form.Item>

				<section className='form-panel export-editor__panel'>
					<div className='micro-label form-panel__kicker'>What it exports</div>
					<Segmented
						block
						options={kindOptions}
						value={kind}
						onChange={handleKindChange}
						aria-label='What it exports'
					/>

					{kind === 'items' ? (
						<Space direction='vertical' size={8} className='export-editor__stack'>
							<Select
								value={itemsValue}
								options={itemsOptions}
								onChange={handleItemsChange}
								status={fieldStatus( 'subject' )}
								aria-label='Which items'
							/>
							{draft.itemsSource === 'filter' ? (
								<Text type='secondary' className='form-panel__hint'>
									The saved filter is read fresh on every run, so changing the filter changes what goes out.
								</Text>
							) : (<>
								<Select
									allowClear
									placeholder='All cards'
									value={draft.params?.card_id ? normalizeId( draft.params.card_id ) : undefined}
									options={cardOptions}
									onChange={value => patch( { params: { ...draft.params, card_id: value || undefined } } )}
									aria-label='Card'
								/>
								{draft.params?.q || draft.params?.sort_field ? (
									<div className='export-editor__carried'>
										{draft.params?.q ? (
											<FilterChips query={draft.params.q} fields={filterFields} readOnly />
										) : null}
										<Text type='secondary' className='form-panel__hint'>
											Filtered and sorted as the list was when it was saved.
										</Text>
										<Button
											size='small'
											type='link'
											className='export-editor__clear'
											onClick={() => patch( { params: { card_id: draft.params?.card_id } } )}
										>
											Clear the filter and sort
										</Button>
									</div>
								) : null}
							</>)}
						</Space>
					) : null}

					{kind === 'card' ? (<>
						<Select
							className='export-editor__stack'
							placeholder='Choose a card definition'
							value={draft.cardId || undefined}
							options={cardOptions}
							onChange={value => patch( { cardId: value } )}
							status={fieldStatus( 'subject' )}
							aria-label='Card definition'
						/>
						<Text type='secondary' className='form-panel__hint'>
							Documents the card definition and its elements. It holds no items.
						</Text>
					</>) : null}

					{kind === 'collection' ? (
						<Text type='secondary' className='form-panel__hint'>
							A one-page outline of the collection and the cards it uses. It holds no items.
						</Text>
					) : null}

					{kind === 'item' ? (
						<Text type='secondary' className='form-panel__hint'>
							One item, chosen when this export was created.
						</Text>
					) : null}

					{errors.subject ? <div className='ant-form-item-explain-error' role='alert'>{errors.subject}</div> : null}
				</section>

				<section className='form-panel export-editor__panel'>
					<div className='micro-label form-panel__kicker'>Format</div>
					<div className='export-modal__controls'>
						<ExportOptionsFields
							format={draft.format}
							onFormatChange={value => patch( { format: value } )}
							contents={controls.contents}
							onContentsChange={value => patchControls( { contents: value } )}
							attachmentList={controls.attachmentList}
							onAttachmentListChange={value => patchControls( { attachmentList: value } )}
							embedAttachments={controls.embedAttachments}
							onEmbedAttachmentsChange={value => patchControls( { embedAttachments: value } )}
							blankForm={controls.blankForm}
							onBlankFormChange={value => patchControls( { blankForm: value } )}
							groupShape={controls.groupShape}
							onGroupShapeChange={value => patchControls( { groupShape: value } )}
							offersGroupShape={offersGroupShape}
							supportsExcel={kind === 'items'}
							supportsAttachmentOptions={kind === 'item'}
							isItemsTarget={kind === 'items'}
							report={report}
							listOptions={STORED_LIST_OPTIONS}
						/>
					</div>
					{kind === 'items' ? (
						<Text type='secondary' className='form-panel__hint'>
							A run that reaches the limit says so on its run and in the email.
						</Text>
					) : null}
					{pivotIsStored && kind === 'items' && draft.format === 'pdf' && controls.contents === 'matrix' ? (
						<div className='export-editor__carried' data-testid='export-editor-stored-pivot'>
							<Text type='secondary' className='form-panel__hint'>
								{describePivot( report, labelFor )}
							</Text>
							<Text type='secondary' className='form-panel__hint'>
								This grid was set up outside this editor. It is kept as it is when you save.
							</Text>
						</div>
					) : null}
					{errors.format ? <div className='ant-form-item-explain-error' role='alert'>{errors.format}</div> : null}
				</section>

				<section className='form-panel export-editor__panel'>
					<div className='micro-label form-panel__kicker'>When it runs</div>
					<ToggleRow
						first
						id='exportScheduleOn'
						label='Run on a schedule'
						description='Off: it runs only when its owner chooses Run now.'
						checked={Boolean( draft.schedule )}
						onChange={on => patch( { schedule: on ? defaultSchedule() : null } )}
					/>
					{draft.schedule ? (
						<div className='export-editor__schedule'>
							<ScheduleFields
								value={draft.schedule}
								onChange={next => patch( { schedule: next } )}
								idPrefix='exportSchedule'
								refusedUnits={bounds.refusedScheduleUnits}
							/>
						</div>
					) : null}
					{errors.schedule ? <div className='ant-form-item-explain-error' role='alert'>{errors.schedule}</div> : null}
				</section>

				<section className='form-panel export-editor__panel'>
					<div className='form-panel__kicker-row'>
						<div className='micro-label form-panel__kicker'>Who receives it</div>
						<span className='form-panel__count'>{`${ recipientCount } of ${ bounds.maxRecipients }`}</span>
					</div>

					<Form.Item
						label='People in this collection'
						help='Anyone with access to the whole collection. Someone with access to only some cards cannot receive it.'
					>
						<Select
							mode='multiple'
							allowClear
							loading={recipientsLoading}
							value={draft.accountIds}
							options={accountOptions}
							onChange={value => patch( { accountIds: value } )}
							filterOption={( input, option ) => ( option?.searchText || '' ).includes( input.toLowerCase() )}
							placeholder='Choose people'
							notFoundContent={recipientsLoading ? 'Loading…' : 'Nobody else has access to the whole collection.'}
							aria-label='People in this collection'
						/>
					</Form.Item>

					<Form.Item
						label={`Email addresses (${ draft.addresses.length } of ${ bounds.maxExternalRecipients })`}
						help='For people without a keepr account.'
					>
						<Space.Compact className='export-editor__address'>
							<Input
								{...NO_TEXT_ASSIST}
								value={newAddress}
								onChange={event => setNewAddress( event.target.value )}
								onPressEnter={event => { event.preventDefault(); addAddress(); }}
								placeholder='bookkeeper@example.com'
								type='email'
								inputMode='email'
								aria-label='Add an email address'
							/>
							<Button icon={<PLUS />} onClick={addAddress}>Add</Button>
						</Space.Compact>
						{draft.addresses.length ? (
							<div className='export-editor__addresses'>
								{draft.addresses.map( entry => (
									<Tag
										key={entry.address}
										closable
										closeIcon={<CLOSE />}
										onClose={event => {
											event.preventDefault();
											patch( { addresses: draft.addresses.filter( row => row.address !== entry.address ) } );
										}}
									>
										{entry.label ? `${ entry.label } · ${ entry.address }` : entry.address}
									</Tag>
								) )}
							</div>
						) : null}
					</Form.Item>

					{errors.recipients ? <div className='ant-form-item-explain-error' role='alert'>{errors.recipients}</div> : null}

					<Text type='secondary' className='form-panel__hint'>
						{recipientCount
							? 'Each recipient gets a link of their own. Removing someone stops their links at once, and the email lets anyone take themselves off the list.'
							: 'Nobody yet: each run is kept for its owner to download from Runs.'}
					</Text>
				</section>

				<section className='form-panel export-editor__panel'>
					<div className='micro-label form-panel__kicker'>How it reaches them</div>
					<ToggleRow
						first
						id='exportChannelEmail'
						label='Email'
						description={email.available === false
							? ( CHANNEL_REASON_WORDS[ email.reason ] || 'Email cannot be sent right now.' )
							: 'A download link, never an attachment. People who turned off Export emails are skipped.'}
						checked={draft.emailEnabled}
						onChange={value => patch( { emailEnabled: value } )}
					/>
					<ToggleRow
						label='Text message (RCS)'
						description={rcs.available === false
							? ( CHANNEL_REASON_WORDS[ rcs.reason ] || 'Not available.' )
							: null}
					>
						<Switch checked={false} disabled aria-label='Text message (RCS), not available' />
					</ToggleRow>
					{errors.channels ? <div className='ant-form-item-explain-error' role='alert'>{errors.channels}</div> : null}
				</section>

				<section className='form-panel export-editor__panel'>
					<div className='micro-label form-panel__kicker'>Keeping the file</div>
					<div className='export-editor__keep'>
						<Form.Item
							label='Keep each file for'
							validateStatus={fieldStatus( 'retentionDays' )}
							help={errors.retentionDays || ( draft.retentionClampedFrom
								? `This export kept files ${ draft.retentionClampedFrom } days. keepr now keeps them at most ${ bounds.maxRetentionDays }.`
								: `Then keepr deletes it. At most ${ bounds.maxRetentionDays } days.` )}
						>
							<InputNumber
								min={1}
								max={bounds.maxRetentionDays}
								precision={0}
								inputMode='numeric'
								value={draft.retentionDays}
								onChange={value => patch( { retentionDays: value } )}
								suffix='days'
								aria-label='Keep each file for'
							/>
						</Form.Item>
						<Form.Item
							label='Links stop working after'
							validateStatus={fieldStatus( 'linkExpiresDays' )}
							help={errors.linkExpiresDays || 'Never later than the file is kept.'}
						>
							<InputNumber
								min={1}
								max={Math.max( 1, Number( draft.retentionDays ) || 1 )}
								precision={0}
								inputMode='numeric'
								value={Math.min( Number( draft.linkExpiresDays ) || 1, Math.max( 1, Number( draft.retentionDays ) || 1 ) )}
								onChange={value => patch( { linkExpiresDays: value } )}
								suffix='days'
								aria-label='Links stop working after'
							/>
						</Form.Item>
					</div>
				</section>

				{formError ? <Alert type='error' showIcon message={formError} /> : null}
				<QueryRefusalPick
					inline
					refusal={elementPicks.refusal}
					disabled={saving}
					onPick={value => { elementPicks.pick( value ); handleSave(); }}
					onRepick={() => { elementPicks.clearRefusal(); handleSave(); }}
				/>

			</Form>
		</Modal>
	);
}
